import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getEscrows, 
  exportEscrows,
  getEscrowStatusOptions,
  formatEscrowAmount 
} from '../../services/api';
import { 
  FiSearch, 
  FiFilter, 
  FiRefreshCw, 
  FiEye, 
  FiCheckCircle, 
  FiXCircle, 
  FiArrowLeft, 
  FiArrowRight,
  FiDownload,
  FiAlertCircle,
  FiDollarSign,
  FiMessageSquare,
  FiShoppingCart,
  FiClock,
  FiCheck,
  FiX,
  FiAlertTriangle,
  FiTrendingUp,
  FiTrendingDown
} from 'react-icons/fi';

function Escrows() {
  const [escrows, setEscrows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    search: '',
    date_from: '',
    date_to: '',
    min_amount: '',
    max_amount: '',
    user_id: '',
    user_role: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [showFilters, setShowFilters] = useState(false);
  const [stats, setStats] = useState({
    total_amount: 0,
    total_platform_fees: 0,
    total_escrows: 0,
    held_amount: 0
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchEscrows();
  }, [filters, pagination.page]);

  const fetchEscrows = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await getEscrows(params);
      if (response.success) {
        setEscrows(response.data.escrows || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0,
          pages: response.data.pagination?.pages || 0
        }));
        
        // Calculate stats from current page
        calculateStats(response.data.escrows || []);
      } else {
        setError(response.message || 'Failed to load escrows');
      }
    } catch (err) {
      setError('Failed to load escrows: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (escrowList) => {
    const stats = {
      total_amount: 0,
      total_platform_fees: 0,
      total_escrows: escrowList.length,
      held_amount: 0,
      released_amount: 0,
      refunded_amount: 0
    };

    escrowList.forEach(escrow => {
      stats.total_amount += parseFloat(escrow.amount || 0);
      stats.total_platform_fees += parseFloat(escrow.platform_fee_total || 0);
      
      if (escrow.status === 'held') {
        stats.held_amount += parseFloat(escrow.amount || 0);
      } else if (escrow.status === 'released') {
        stats.released_amount += parseFloat(escrow.seller_receives_net || 0);
      } else if (escrow.status === 'refunded') {
        stats.refunded_amount += parseFloat(escrow.amount || 0);
      }
    });

    setStats(stats);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-3 py-1 text-xs font-medium rounded-full flex items-center justify-center w-28";
    
    const statusConfig = {
      'pending': { bg: 'bg-yellow-900/30', text: 'text-yellow-400 border-yellow-800/50', label: 'Pending', icon: <FiClock className="mr-1" /> },
      'held': { bg: 'bg-blue-900/30', text: 'text-blue-400 border-blue-800/50', label: 'Held', icon: <FiClock className="mr-1" /> },
      'released': { bg: 'bg-green-900/30', text: 'text-green-400 border-green-800/50', label: 'Released', icon: <FiCheck className="mr-1" /> },
      'refunded': { bg: 'bg-red-900/30', text: 'text-red-400 border-red-800/50', label: 'Refunded', icon: <FiX className="mr-1" /> },
      'disputed': { bg: 'bg-[#ff660e]/20', text: 'text-[#ff660e] border-[#ff660e]/30', label: 'Disputed', icon: <FiAlertTriangle className="mr-1" /> },
      'cancelled': { bg: 'bg-gray-700', text: 'text-gray-300 border-gray-600', label: 'Cancelled', icon: <FiX className="mr-1" /> },
      'expired': { bg: 'bg-gray-700', text: 'text-gray-400 border-gray-600', label: 'Expired', icon: <FiClock className="mr-1" /> }
    };

    const config = statusConfig[status] || { bg: 'bg-gray-700', text: 'text-gray-400', label: status, icon: '?' };
    
    return (
      <span className={`${baseClasses} ${config.bg} ${config.text} border ${config.border || 'border-transparent'}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  const getTypeBadge = (escrow) => {
    if (escrow.order_id) {
      return (
        <span className="px-3 py-1 bg-purple-900/30 text-purple-400 text-sm font-medium rounded-full border border-purple-800/50 flex items-center">
          <FiShoppingCart className="mr-1" />
          Order
        </span>
      );
    } else if (escrow.conversation_id) {
      return (
        <span className="px-3 py-1 bg-indigo-900/30 text-indigo-400 text-sm font-medium rounded-full border border-indigo-800/50 flex items-center">
          <FiMessageSquare className="mr-1" />
          Chat
        </span>
      );
    }
    return (
      <span className="px-3 py-1 bg-gray-700 text-gray-300 text-sm font-medium rounded-full border border-gray-600">
        Other
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDisputeBadge = (escrow) => {
    if (escrow.dispute_status) {
      return (
        <span className="px-2 py-0.5 bg-[#ff660e]/20 text-[#ff660e] text-xs font-medium rounded-full border border-[#ff660e]/30">
          Dispute: {escrow.dispute_status}
        </span>
      );
    }
    return null;
  };

  const handleExport = async () => {
    try {
      const params = { ...filters };
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await exportEscrows(params);
      
      // Create blob and download
      const blob = new Blob([response], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `escrows_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      setSuccessMessage('Export completed successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to export: ' + err.message);
    }
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      type: '',
      search: '',
      date_from: '',
      date_to: '',
      min_amount: '',
      max_amount: '',
      user_id: '',
      user_role: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const getStatusOptions = () => {
    return getEscrowStatusOptions();
  };

  // Loading Skeleton
  if (loading && escrows.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-700 rounded w-1/2 mb-8"></div>
          
          {/* Stats Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-800 rounded-xl border border-gray-700 p-5">
                <div className="h-4 bg-gray-700 rounded w-1/2 mb-4"></div>
                <div className="h-8 bg-gray-700 rounded w-1/4 mb-2"></div>
                <div className="h-4 bg-gray-700 rounded w-3/4"></div>
              </div>
            ))}
          </div>

          {/* Table Skeleton */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="p-6 border-b border-gray-700">
                <div className="flex justify-between items-center">
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-700 rounded w-48"></div>
                    <div className="h-3 bg-gray-700 rounded w-32"></div>
                  </div>
                  <div className="h-6 bg-gray-700 rounded w-20"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-900 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              <span className="text-[#ff660e]">Escrow</span> Management
            </h1>
            <p className="text-gray-400 mt-1">Secure payment escrow system for buyer and seller protection</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/admin/escrows/statistics')}
              className="px-4 py-2.5 bg-gray-800 text-white border border-gray-700 rounded-lg hover:bg-gray-700 hover:border-[#ff660e] transition-all duration-200 font-medium flex items-center hover:scale-[1.02]"
            >
              <FiTrendingUp className="mr-2" />
              Statistics
            </button>
            <button
              onClick={handleExport}
              className="px-4 py-2.5 bg-gray-800 text-white border border-gray-700 rounded-lg hover:bg-gray-700 hover:border-[#ff660e] transition-all duration-200 font-medium flex items-center hover:scale-[1.02]"
            >
              <FiDownload className="mr-2" />
              Export CSV
            </button>
            <button
              onClick={fetchEscrows}
              className="px-4 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-all duration-200 font-medium flex items-center hover:scale-[1.02] shadow-lg"
            >
              <FiRefreshCw className="mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-900/20 border border-green-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiCheckCircle className="text-green-400 text-xl" />
            <p className="text-green-300 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-400 hover:text-green-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiAlertCircle className="text-red-400 text-xl" />
            <p className="text-red-300">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-400 hover:text-red-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Total Escrows</p>
              <p className="text-2xl font-bold text-white mt-1 group-hover:text-[#ff660e] transition-colors">{pagination.total}</p>
              <p className="text-sm text-gray-500 mt-1">{formatEscrowAmount(stats.total_amount)}</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-blue-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiDollarSign className="text-blue-400 text-2xl" />
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Currently Held</p>
              <p className="text-2xl font-bold text-blue-400 mt-1 group-hover:text-blue-300 transition-colors">{formatEscrowAmount(stats.held_amount)}</p>
              <p className="text-sm text-gray-500 mt-1">In secure escrow</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-blue-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiClock className="text-blue-400 text-2xl" />
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Released to Sellers</p>
              <p className="text-2xl font-bold text-green-400 mt-1 group-hover:text-green-300 transition-colors">{formatEscrowAmount(stats.released_amount)}</p>
              <p className="text-sm text-gray-500 mt-1">Successfully paid out</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-green-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiTrendingUp className="text-green-400 text-2xl" />
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Refunded to Buyers</p>
              <p className="text-2xl font-bold text-red-400 mt-1 group-hover:text-red-300 transition-colors">{formatEscrowAmount(stats.refunded_amount)}</p>
              <p className="text-sm text-gray-500 mt-1">Issued as refunds</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-red-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiTrendingDown className="text-red-400 text-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters Card */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 mb-6">
        <div className="p-4 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FiFilter className="text-[#ff660e]" />
              <h3 className="font-medium text-white">Filters</h3>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="text-sm text-[#ff660e] hover:text-orange-400 font-medium flex items-center transition-colors"
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="p-4 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              {/* Search */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">Search</label>
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Listing title, buyer, seller, reference..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="w-full pl-10 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white placeholder-gray-500"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white"
                >
                  <option value="">All Status</option>
                  {getStatusOptions().map(option => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Type</label>
                <select
                  value={filters.type}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white"
                >
                  <option value="">All Types</option>
                  <option value="order">Order-based</option>
                  <option value="chat">Chat-initiated</option>
                </select>
              </div>

              {/* User Role */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">User Role</label>
                <select
                  value={filters.user_role}
                  onChange={(e) => handleFilterChange('user_role', e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white"
                >
                  <option value="">All Roles</option>
                  <option value="buyer">Buyer</option>
                  <option value="seller">Seller</option>
                </select>
              </div>

              {/* Date From */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">From Date</label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => handleFilterChange('date_from', e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white"
                />
              </div>

              {/* Date To */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">To Date</label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => handleFilterChange('date_to', e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white"
                />
              </div>

              {/* Min Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Min Amount (₦)</label>
                <input
                  type="number"
                  placeholder="1000"
                  value={filters.min_amount}
                  onChange={(e) => handleFilterChange('min_amount', e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white placeholder-gray-500"
                />
              </div>

              {/* Max Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Max Amount (₦)</label>
                <input
                  type="number"
                  placeholder="1000000"
                  value={filters.max_amount}
                  onChange={(e) => handleFilterChange('max_amount', e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white placeholder-gray-500"
                />
              </div>

              {/* User ID */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">User ID</label>
                <input
                  type="number"
                  placeholder="User ID"
                  value={filters.user_id}
                  onChange={(e) => handleFilterChange('user_id', e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white placeholder-gray-500"
                />
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex justify-between items-center mt-6 pt-6 border-t border-gray-700">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-gray-400 hover:text-gray-300 text-sm font-medium hover:bg-gray-700 rounded-lg transition-colors"
              >
                Clear All Filters
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowFilters(false)}
                  className="px-4 py-2 text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors font-medium hover:scale-[1.02]"
                >
                  Cancel
                </button>
                <button
                  onClick={fetchEscrows}
                  className="px-4 py-2 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium flex items-center hover:scale-[1.02]"
                >
                  <FiFilter className="mr-2" />
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Escrows Table */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Escrow Details
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Parties
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Amount & Fees
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Status & Timeline
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-700">
              {escrows.map((escrow) => (
                <tr key={escrow.escrow_id} className="hover:bg-gray-750 transition-colors duration-150 group">
                  <td className="px-6 py-4">
                    <div>
                      <div className="flex items-center space-x-2 mb-2">
                        {getTypeBadge(escrow)}
                        {getStatusBadge(escrow.status)}
                        {getDisputeBadge(escrow)}
                      </div>
                      <div className="text-sm font-semibold text-white truncate max-w-xs group-hover:text-[#ff660e] transition-colors">
                        {escrow.listing_title || 'Chat Escrow'}
                      </div>
                      <div className="text-xs text-gray-500 mt-1 space-y-1">
                        <div>ID: {escrow.escrow_id}</div>
                        {escrow.order_id && <div>Order: #{escrow.order_id}</div>}
                        {escrow.conversation_id && <div>Chat: #{escrow.conversation_id}</div>}
                        {escrow.payment_reference && (
                          <div className="font-mono text-xs text-gray-400">Ref: {escrow.payment_reference}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-3">
                      {/* Buyer */}
                      <div>
                        <div className="text-xs text-gray-400 mb-1">Buyer</div>
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 flex items-center justify-center text-white text-xs font-bold">
                            {escrow.buyer_first_name?.charAt(0)}{escrow.buyer_last_name?.charAt(0)}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-white group-hover:text-[#ff660e] transition-colors">
                              {escrow.buyer_first_name} {escrow.buyer_last_name}
                            </div>
                            <div className="text-xs text-gray-400">
                              ID: {escrow.buyer_id}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Seller */}
                      <div>
                        <div className="text-xs text-gray-400 mb-1">Seller</div>
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-orange-500 to-red-600 flex items-center justify-center text-white text-xs font-bold">
                            {escrow.seller_first_name?.charAt(0)}{escrow.seller_last_name?.charAt(0)}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-white group-hover:text-[#ff660e] transition-colors">
                              {escrow.seller_first_name} {escrow.seller_last_name}
                            </div>
                            <div className="text-xs text-gray-400">
                              {escrow.seller_store_name && (
                                <div className="font-medium text-gray-300">{escrow.seller_store_name}</div>
                              )}
                              ID: {escrow.seller_id}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-2">
                      <div className="text-2xl font-bold text-blue-400 group-hover:text-blue-300 transition-colors">
                        {formatEscrowAmount(escrow.amount)}
                      </div>
                      <div className="text-xs text-gray-500 space-y-1">
                        <div className="flex justify-between">
                          <span>Seller Receives:</span>
                          <span className="font-medium text-gray-300">{formatEscrowAmount(escrow.seller_receives_net)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Platform Fee:</span>
                          <span>{formatEscrowAmount(escrow.platform_fee_total)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Payment Fee:</span>
                          <span>{formatEscrowAmount(escrow.paystack_fee_total)}</span>
                        </div>
                        {escrow.fee_deducted > 0 && (
                          <div className="flex justify-between">
                            <span>Fees Deducted:</span>
                            <span className="text-red-400">{formatEscrowAmount(escrow.fee_deducted)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm space-y-2">
                      <div>
                        <div className="font-medium text-white">
                          Created: {formatDate(escrow.created_at)}
                        </div>
                        <div className="text-gray-500 text-xs">
                          {formatTime(escrow.created_at)}
                        </div>
                      </div>
                      {escrow.released_at && (
                        <div>
                          <div className="font-medium text-green-400">
                            Released: {formatDate(escrow.released_at)}
                          </div>
                          <div className="text-gray-500 text-xs">
                            {formatTime(escrow.released_at)}
                          </div>
                        </div>
                      )}
                      {escrow.refunded_at && (
                        <div>
                          <div className="font-medium text-red-400">
                            Refunded: {formatDate(escrow.refunded_at)}
                          </div>
                          <div className="text-gray-500 text-xs">
                            {formatTime(escrow.refunded_at)}
                          </div>
                        </div>
                      )}
                      {escrow.dispute_opened_at && (
                        <div>
                          <div className="font-medium text-[#ff660e]">
                            Disputed: {formatDate(escrow.dispute_opened_at)}
                          </div>
                          <div className="text-gray-500 text-xs">
                            {formatTime(escrow.dispute_opened_at)}
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col space-y-2 min-w-[180px]">
                      <button
                        onClick={() => navigate(`/admin/escrows/${escrow.escrow_id}`)}
                        className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded-lg text-sm font-medium flex items-center justify-center transition-all duration-200 hover:scale-[1.02]"
                      >
                        <FiEye className="mr-2" />
                        View Details
                      </button>
                      
                      {['held', 'disputed'].includes(escrow.status) && (
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => navigate(`/admin/escrows/${escrow.escrow_id}?action=release`)}
                            className="px-3 py-1 bg-green-900/30 hover:bg-green-900/50 text-green-400 hover:text-green-300 rounded text-xs font-medium border border-green-800/50 transition-all duration-200 hover:scale-[1.02]"
                          >
                            <FiTrendingUp className="inline mr-1" />
                            Release Funds
                          </button>
                          <button
                            onClick={() => navigate(`/admin/escrows/${escrow.escrow_id}?action=refund`)}
                            className="px-3 py-1 bg-red-900/30 hover:bg-red-900/50 text-red-400 hover:text-red-300 rounded text-xs font-medium border border-red-800/50 transition-all duration-200 hover:scale-[1.02]"
                          >
                            <FiTrendingDown className="inline mr-1" />
                            Refund Buyer
                          </button>
                        </div>
                      )}
                      
                      {escrow.dispute_status && (
                        <button
                          onClick={() => navigate(`/admin/escrows/disputes/${escrow.dispute_id}`)}
                          className="px-3 py-1 bg-[#ff660e]/20 hover:bg-[#ff660e]/30 text-[#ff660e] hover:text-orange-400 rounded text-xs font-medium border border-[#ff660e]/30 transition-all duration-200 hover:scale-[1.02]"
                        >
                          <FiAlertTriangle className="inline mr-1" />
                          Resolve Dispute
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {escrows.length === 0 && !loading && (
          <div className="text-center py-16">
            <div className="text-gray-600 text-8xl mb-6">💰</div>
            <h3 className="text-xl font-medium text-gray-300 mb-2">No escrows found</h3>
            <p className="text-gray-500 mb-6">Try adjusting your search or filters to find what you're looking for.</p>
            <button
              onClick={clearFilters}
              className="px-6 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium hover:scale-[1.02]"
            >
              Clear Filters
            </button>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-gray-900 px-6 py-4 border-t border-gray-700">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-400">
                Showing <span className="font-semibold text-white">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                <span className="font-semibold text-white">
                  {Math.min(pagination.page * pagination.limit, pagination.total)}
                </span>{' '}
                of <span className="font-semibold text-white">{pagination.total}</span> escrows
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 border border-gray-700 rounded-lg text-sm font-medium text-gray-400 bg-gray-800 hover:bg-gray-700 hover:text-white hover:border-[#ff660e] disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-all duration-200 hover:scale-[1.02]"
                >
                  <FiArrowLeft className="mr-2" />
                  Previous
                </button>
                
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    let pageNum;
                    if (pagination.pages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.pages - 2) {
                      pageNum = pagination.pages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-10 h-10 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105 ${
                          pagination.page === pageNum
                            ? 'bg-[#ff660e] text-white shadow-lg'
                            : 'border border-gray-700 text-gray-400 hover:bg-gray-700 hover:text-white'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="px-4 py-2 border border-gray-700 rounded-lg text-sm font-medium text-gray-400 bg-gray-800 hover:bg-gray-700 hover:text-white hover:border-[#ff660e] disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-all duration-200 hover:scale-[1.02]"
                >
                  Next
                  <FiArrowRight className="ml-2" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default Escrows;